import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiSend } from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import KycBanner from '../../components/KycBanner/KycBanner';
import { useAppSelector } from '../../app/hooks';
import axiosClient from '../../api/axiosClient';
import styles from '../PortalInvestments/PortalInvestments.module.css';

interface TokenBalance {
  investment_id: string;
  portal: 'A' | 'B' | 'C';
  investment_status: string;
  allocated_tokens: string;
  reserved_tokens: string;
  completed_tokens: string;
  available_tokens: string;
}

interface TokenClaim {
  id: string;
  investment_id: string;
  portal: 'A' | 'B' | 'C';
  token_amount: string;
  wallet_address: string;
  status: string;
  rejection_reason: string | null;
  failure_reason: string | null;
  transaction_hash: string | null;
  created_at: string;
  reviewed_at: string | null;
  processing_at: string | null;
  completed_at: string | null;
}

interface ClaimRequest {
  investmentId: string;
  tokenAmount: string;
  walletAddress: string;
  requestKey: string;
}

const PAGE_SIZE = 20;

const STATUS_LABELS: Record<string, string> = {
  pending: 'Awaiting review',
  approved: 'Approved — awaiting transfer',
  processing: 'Transfer processing',
  completed: 'Transfer completed',
  rejected: 'Rejected',
  failed: 'Needs admin review',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: 12,
  borderRadius: 8,
  border: '1px solid var(--line)',
  background: 'var(--card-bg-soft)',
  color: 'var(--cream)',
  font: 'inherit',
};

const labelStyle: React.CSSProperties = {
  display: 'grid',
  gap: 8,
  marginBottom: 16,
};

function quantity(value: string) {
  const [whole, fraction = ''] = String(value).split('.');
  const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimal = fraction.replace(/0+$/, '');

  return decimal ? `${formatted}.${decimal}` : formatted;
}

function tokenUnits(value: string) {
  if (!/^\d{1,12}(\.\d{1,6})?$/.test(value)) return null;

  const [whole, fraction = ''] = value.split('.');

  return `${whole}${fraction.padEnd(6, '0')}`
    .replace(/^0+/, '') || '0';
}

function exceedsBalance(amount: string, balance: string) {
  const requested = tokenUnits(amount);
  const available = tokenUnits(balance);

  if (requested === null || available === null) return true;

  return requested.length > available.length
    || (
      requested.length === available.length
      && requested > available
    );
}

function dateLabel(value: string | null) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function createRequestKey() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);

  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;

  const hex = Array.from(
    bytes,
    (byte) => byte.toString(16).padStart(2, '0'),
  ).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

function getError(error: unknown, fallback: string) {
  const candidate = error as {
    response?: {
      status?: number;
      data?: { message?: string };
    };
  };

  return {
    status: candidate.response?.status,
    message: candidate.response?.data?.message || fallback,
  };
}

const BctTokenClaims: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const storageKey = user?.id ? `bct-claim-pending:${user.id}` : '';

  const [balances, setBalances] = useState<TokenBalance[]>([]);
  const [price, setPrice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [balanceError, setBalanceError] = useState('');

  const [claims, setClaims] = useState<TokenClaim[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const [investmentId, setInvestmentId] = useState('');
  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [pendingRequest, setPendingRequest] =
    useState<ClaimRequest | null>(null);
  const [storageReady, setStorageReady] = useState(false);
  const [storageError, setStorageError] = useState('');

  const submitLock = useRef(false);

  useEffect(() => {
    setStorageReady(false);
    setPendingRequest(null);
    setStorageError('');
    setInvestmentId('');
    setAmount('');
    setWallet('');
    setConfirmed(false);

    if (!storageKey) return;

    try {
      const saved = sessionStorage.getItem(storageKey);

      if (saved) {
        const parsed = JSON.parse(saved) as ClaimRequest;

        if (
          !parsed
          || typeof parsed.investmentId !== 'string'
          || typeof parsed.tokenAmount !== 'string'
          || typeof parsed.walletAddress !== 'string'
          || typeof parsed.requestKey !== 'string'
        ) {
          throw new Error('Invalid saved request');
        }

        const restored: ClaimRequest = {
          investmentId: parsed.investmentId,
          tokenAmount: parsed.tokenAmount,
          walletAddress: parsed.walletAddress,
          requestKey: parsed.requestKey,
        };

        setPendingRequest(restored);
        setInvestmentId(restored.investmentId);
        setAmount(restored.tokenAmount);
        setWallet(restored.walletAddress);
      }

      setStorageReady(true);
    } catch {
      setStorageError(
        'Could not restore the saved request. Contact support before creating another claim.',
      );
    }
  }, [storageKey]);

  useEffect(() => {
    if (!user?.id) return;

    let active = true;
    setLoading(true);
    setBalanceError('');

    axiosClient.get('/bct-token/balance')
      .then(({ data }) => {
        if (!Array.isArray(data.balances)) {
          throw new Error('Invalid balance response');
        }

        if (!active) return;

        setBalances(data.balances);
        setPrice(
          data.bctPriceUsdt == null ? null : String(data.bctPriceUsdt),
        );
      })
      .catch((error: unknown) => {
        if (active) {
          setBalanceError(
            getError(error, 'Could not load token balances.').message,
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [refresh, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    let active = true;
    setHistoryLoading(true);
    setHistoryError('');

    axiosClient.get('/bct-token/claims', {
      params: { limit: PAGE_SIZE, offset },
    })
      .then(({ data }) => {
        if (!Array.isArray(data.claims)) {
          throw new Error('Invalid claim history response');
        }

        if (active) {
          setClaims(data.claims);
          setHasMore(data.hasMore === true);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setHistoryError(
            getError(error, 'Could not load claim history.').message,
          );
        }
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [offset, refresh, user?.id]);

  const selected = balances.find(
    (balance) => balance.investment_id === investmentId,
  );

  const eligible = selected
    && ['active', 'completed'].includes(selected.investment_status);

  const reload = () => setRefresh((value) => value + 1);

  const clearSavedRequest = () => {
    sessionStorage.removeItem(storageKey);
    setPendingRequest(null);
  };

  const submitClaim = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (submitLock.current || !storageReady || !storageKey) return;

    let payload = pendingRequest;

    if (!payload) {
      if (loading || balanceError || !selected || !eligible) {
        toast.error('Select an eligible investment.');
        return;
      }

      const tokenAmount = amount.trim();
      const units = tokenUnits(tokenAmount);

      if (units === null || units === '0') {
        toast.error('Enter a positive quantity with up to six decimal places.');
        return;
      }

      if (exceedsBalance(tokenAmount, selected.available_tokens)) {
        toast.error('The quantity exceeds your available tokens.');
        return;
      }

      const walletAddress = wallet.trim();

      if (
        !walletAddress
        || walletAddress.length > 255
        || /[\s\u0000-\u001f\u007f]/.test(walletAddress)
      ) {
        toast.error('Enter your receiving wallet address without spaces.');
        return;
      }

      if (!confirmed) {
        toast.error('Confirm your receiving wallet address.');
        return;
      }

      try {
        payload = {
          investmentId,
          tokenAmount,
          walletAddress,
          requestKey: createRequestKey(),
        };

        sessionStorage.setItem(storageKey, JSON.stringify(payload));
      } catch {
        toast.error('Could not save the request safely. Claim was not submitted.');
        return;
      }

      setPendingRequest(payload);
    }

    submitLock.current = true;
    setSubmitting(true);

    try {
      const { data } = await axiosClient.post('/bct-token/claims', payload);

      try {
        clearSavedRequest();
      } catch {
        setStorageReady(false);
        setStorageError(
          'Claim submitted, but the saved request could not be cleared. Check claim history before continuing.',
        );
      }

      setAmount('');
      setWallet('');
      setConfirmed(false);
      setOffset(0);
      reload();

      toast.success(data.message || 'Claim submitted for admin review.');
    } catch (error: unknown) {
      const result = getError(error, 'Could not confirm the claim submission.');

      if ([400, 403, 404].includes(result.status || 0)) {
        try {
          clearSavedRequest();
        } catch {
          setStorageReady(false);
          setStorageError('Could not clear the saved request. Contact support.');
        }

        toast.error(result.message);
      } else {
        toast.error(
          `${result.message} Retry the saved request before starting another claim.`,
        );
      }

      reload();
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell>
      <PageHeader
        title="BCT100 Claims"
        subtitle="Submit your wallet address and track your token claim."
      />

      <div className={styles.page}>
        <KycBanner />

        <button
          type="button"
          className={styles.expandBtn}
          style={{ marginBottom: 20 }}
          disabled={loading || historyLoading || submitting}
          onClick={reload}
        >
          <FiRefreshCw /> Refresh
        </button>

        <div className={styles.card}>
          <h3>BCT100 Price</h3>
          <p>
            {loading
              ? 'Loading…'
              : balanceError
                ? 'Price unavailable'
                : price && Number(price) > 0
                  ? `1 BCT100 = ${quantity(price)} USDT`
                  : 'Price not available'}
          </p>
          <p className={styles.meta}>
            Current price set by admin. Claims use your allocated token quantity.
          </p>
        </div>

        <div className={styles.card}>
          <h3>Token Balances</h3>

          {loading ? (
            <p className={styles.muted}>Loading token balances…</p>
          ) : balanceError ? (
            <p role="alert">{balanceError}</p>
          ) : balances.length === 0 ? (
            <p>No token allocations are available yet.</p>
          ) : (
            <div className={styles.scheduleWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Investment</th>
                    <th>Allocated</th>
                    <th>Reserved</th>
                    <th>Transferred</th>
                    <th>Available</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map((balance) => (
                    <tr key={balance.investment_id}>
                      <td>
                        Portal {balance.portal}
                        <div className={styles.meta}>
                          {balance.investment_id.slice(0, 8)}
                          {' · '}{balance.investment_status}
                        </div>
                      </td>
                      <td>{quantity(balance.allocated_tokens)}</td>
                      <td>{quantity(balance.reserved_tokens)}</td>
                      <td>{quantity(balance.completed_tokens)}</td>
                      <td>{quantity(balance.available_tokens)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className={styles.meta} style={{ marginTop: 14, lineHeight: 1.7 }}>
            All quantities are BCT100 tokens. Submitted claims reserve tokens.
            Rejection releases them. Claims needing transfer review remain
            reserved until resolved.
          </p>
        </div>

        <div className={styles.card}>
          <h3>Claim BCT100</h3>

          {storageError && <p role="alert">{storageError}</p>}

          {pendingRequest && (
            <div style={{ marginBottom: 18, overflowWrap: 'anywhere' }}>
              <p>
                A saved submission needs confirmation. Retry sends the same
                request key to avoid creating another claim.
              </p>
              <p>Quantity: {quantity(pendingRequest.tokenAmount)} BCT100</p>
              <p>Wallet: {pendingRequest.walletAddress}</p>
              <p className={styles.meta}>
                Request key: {pendingRequest.requestKey}
              </p>
              <p className={styles.meta}>
                If a conflict persists, contact support with this request key.
              </p>
            </div>
          )}

          <form onSubmit={submitClaim}>
            <fieldset
              disabled={submitting || Boolean(pendingRequest)}
              style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
            >
              <label style={labelStyle}>
                Investment
                <select
                  style={inputStyle}
                  value={investmentId}
                  required
                  onChange={(event) => {
                    setInvestmentId(event.target.value);
                    setAmount('');
                    setConfirmed(false);
                  }}
                >
                  <option value="">Select investment</option>
                  {balances.map((balance) => (
                    <option
                      key={balance.investment_id}
                      value={balance.investment_id}
                      disabled={
                        !['active', 'completed'].includes(balance.investment_status)
                      }
                    >
                      Portal {balance.portal}
                      {' — '}{balance.investment_id.slice(0, 8)}
                      {' — '}{quantity(balance.available_tokens)} available
                    </option>
                  ))}
                </select>
              </label>

              <label style={labelStyle}>
                Token Quantity
                <input
                  style={inputStyle}
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  maxLength={19}
                  required
                  placeholder="Enter BCT100 quantity"
                  onChange={(event) => setAmount(event.target.value)}
                />
              </label>

              <label style={labelStyle}>
                Receiving Wallet Address
                <input
                  style={inputStyle}
                  type="text"
                  value={wallet}
                  maxLength={255}
                  autoComplete="off"
                  spellCheck={false}
                  required
                  placeholder="Paste your receiving wallet address"
                  onChange={(event) => {
                    setWallet(event.target.value);
                    setConfirmed(false);
                  }}
                />
              </label>

              <label
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  marginBottom: 20,
                  lineHeight: 1.6,
                }}
              >
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                <span>
                  I have checked that this is my correct receiving wallet address.
                </span>
              </label>
            </fieldset>

            <button
              type="submit"
              className={styles.investBtn}
              disabled={
                submitting
                || !storageReady
                || Boolean(storageError)
                || (
                  !pendingRequest
                  && (
                    loading
                    || Boolean(balanceError)
                    || !eligible
                    || !confirmed
                  )
                )
              }
            >
              <FiSend />
              {submitting
                ? 'Submitting…'
                : pendingRequest
                  ? 'Retry Saved Request'
                  : 'Submit Claim'}
            </button>
          </form>

          <p className={styles.meta} style={{ marginTop: 16, lineHeight: 1.7 }}>
            Admin will review your wallet address and approve or reject the
            request. After approval, tokens will be sent separately.
            Completed claims display the transaction hash recorded by admin.
          </p>
        </div>

        <div className={styles.card}>
          <h3>Claim History</h3>

          {historyLoading ? (
            <p className={styles.muted}>Loading claim history…</p>
          ) : historyError ? (
            <p role="alert">{historyError}</p>
          ) : claims.length === 0 ? (
            <p>No claims on this page.</p>
          ) : (
            <div className={styles.scheduleWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Requested (IST)</th>
                    <th>Portal / Quantity</th>
                    <th>Receiving Wallet</th>
                    <th>Status</th>
                    <th>Transfer / Review Details</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim) => (
                    <tr key={claim.id}>
                      <td>
                        {dateLabel(claim.created_at)}
                        <div className={styles.meta}>
                          {claim.id.slice(0, 8)}
                        </div>
                      </td>
                      <td>
                        Portal {claim.portal}
                        <div>{quantity(claim.token_amount)} BCT100</div>
                      </td>
                      <td>
                        <div
                          style={{
                            overflowWrap: 'anywhere',
                            minWidth: 150,
                            maxWidth: 230,
                          }}
                        >
                          {claim.wallet_address}
                        </div>
                      </td>
                      <td>{STATUS_LABELS[claim.status] || claim.status}</td>
                      <td style={{ minWidth: 180 }}>
                        {claim.rejection_reason && (
                          <div>Reason: {claim.rejection_reason}</div>
                        )}
                        {claim.failure_reason && (
                          <div>Review required: {claim.failure_reason}</div>
                        )}
                        {claim.reviewed_at && (
                          <div>Reviewed: {dateLabel(claim.reviewed_at)}</div>
                        )}
                        {claim.processing_at && (
                          <div>Processing: {dateLabel(claim.processing_at)}</div>
                        )}
                        {claim.completed_at && (
                          <div>Completed: {dateLabel(claim.completed_at)}</div>
                        )}
                        {claim.transaction_hash && (
                          <div
                            style={{
                              overflowWrap: 'anywhere',
                              maxWidth: 260,
                            }}
                          >
                            Transaction: {claim.transaction_hash}
                          </div>
                        )}
                        {claim.status === 'pending' && 'Awaiting admin review.'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginTop: 18,
            }}
          >
            <button
              type="button"
              className={styles.expandBtn}
              disabled={historyLoading || offset === 0}
              onClick={() =>
                setOffset((value) => Math.max(0, value - PAGE_SIZE))
              }
            >
              Previous
            </button>

            <span>Page {Math.floor(offset / PAGE_SIZE) + 1}</span>

            <button
              type="button"
              className={styles.expandBtn}
              disabled={historyLoading || Boolean(historyError) || !hasMore}
              onClick={() => setOffset((value) => value + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
};

export default BctTokenClaims;